import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import { theme as Theme } from '@/assets/theme';
import { ScreenNamesEnum } from '@/constants';
import { numbersToPersian, Paginated, smartDate } from '@matap/shared';
import ObjectId from 'bson-objectid';
import Constants from 'expo-constants';

const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([^\s]+\.(?:com|net|org|ir|io|edu)(?:\/[^\s]*)?)/gi;

export function generateObjectId() {
  return ObjectId().toHexString();
}

export function isRunningOnMobileBrowser() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 800; // or 1024
}

export function isPromise<T>(value: T | Promise<T>): value is Promise<T> {
  return value instanceof Promise;
}

export function getCurrentTime() {
  return toServerTime();
}

export function toServerTime(time?: string | number) {
  return smartDate(time).add(global.timeDifference || 0, 'milliseconds');
}

export function findUrls(text: string): { part: string; isUrl: boolean }[] {
  if (!text) return [];

  // Using matchAll and split logic to preserve order and content
  const matches = [...text.matchAll(urlRegex)];
  const result: { part: string; isUrl: boolean }[] = [];
  let lastIndex = 0;

  matches.forEach((match) => {
    const start = match.index!;
    // Add the plain text before the URL
    if (start > lastIndex) {
      result.push({ part: text.slice(lastIndex, start), isUrl: false });
    }
    // Add the URL part
    result.push({ part: match[0], isUrl: true });
    lastIndex = start + match[0].length;
  });

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    result.push({ part: text.slice(lastIndex), isUrl: true });
  }

  return result;
}

export function maxVal(value: number) {
  return ({ theme }) => `${theme.max(value)}px`;
}

export function minVal(value: number) {
  return ({ theme }) => `${theme.min(value)}px`;
}

export function hpVal(value: number) {
  return ({ theme }) => `${theme.hp(value)}px`;
}

export function wpVal(value: number) {
  return ({ theme }) => `${theme.wp(value)}px`;
}

export function themeVal(fnc: (theme: typeof Theme) => any) {
  return ({ theme }) => fnc(theme);
}

export function isBase64(text: string) {
  return !!(text && text.match(/^data:/));
}

export function isHttpUrl(value: string) {
  return value.startsWith('http://') || value.startsWith('https://');
}

export function isBlobUrl(value: string) {
  return value.startsWith('blob:');
}

export const generateNameFromUrl = (url: string) => {
  url = decodeURIComponent(url);
  url = url.replace(/^https?:\/\//, '');

  // Replace all invalid characters with underscores
  return url.replace(/[/|?*<>":\\]/g, '_');
};

export const callIntent = (phone: string) => {
  const url = `tel://${phone}`;
  openURL(url);
};

export function isSafari() {
  return Platform.OS === 'web' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function getAppVersion() {
  return Constants.expoConfig?.version ?? 'Unknown';
}

export function getSupportedWebAudiRecordFormat() {
  return [
    'audio/mp4',
    'audio/mpeg',
    'audio/mp3',
    'audio/aac',
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/ogg',
    'audio/ogg;codecs=opus',
    'audio/wav',
    'audio/x-wav',
  ].find((format) => MediaRecorder.isTypeSupported(format));
}

export const defaultGetNextParam = ({ hasNextPage, nextPage }: Paginated<any>) => (hasNextPage ? nextPage : undefined);
export const defaultGetPreviousParam = ({ hasPrevPage, prevPage }: Paginated<any>) => (hasPrevPage ? prevPage : undefined);

export function sortDayToJalali(day: number | string) {
  const sorted = ['6', '5', '4', '3', '2', '1', '0'];
  return sorted[Number(day)];
}

export function numberWithCommas(number: number | string) {
  const parts = number.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

export function handleDeepLink(url: string, router: { navigateTo: (...args: any[]) => void }) {
  if (!url) return;
  try {
    const parsedUrl = new URL(url);
    if (Platform.OS === 'web' && parsedUrl.host !== window.location.host) {
      return;
    }
    router.navigateTo(
      parsedUrl.pathname as ScreenNamesEnum,
      Array.from(parsedUrl.searchParams?.keys() || []).reduce((total, current) => {
        total[current] = parsedUrl.searchParams.get(current);
        return total;
      }, {} as any),
    );
  } catch {
    console.warn('Invalid deep link URL:', url);
  }
}

export function openURL(url: string, target = '_blank') {
  if (Platform.OS === 'web') {
    if (target === '_self') {
      Linking.openURL(url);
    } else {
      window.open(url, target);
    }
  } else {
    Linking.openURL(url);
  }
}

export function numbersToEnglish(str: string) {
  if (!str) {
    return str;
  }
  str = String(str);
  let e = '۰'.charCodeAt(0);
  // @ts-ignore
  str = str.replace(/[۰-۹]/g, (t) => t.charCodeAt(0) - e);

  e = '٠'.charCodeAt(0);
  // @ts-ignore
  str = str.replace(/[٠-٩]/g, (t) => t.charCodeAt(0) - e);
  return str;
}

export function scaleImage(originalWidth: number, originalHeight: number, maxWidth: number, maxHeight: number) {
  let scaleFactor = 1;

  if (originalWidth > maxWidth || originalHeight > maxHeight) {
    const widthRatio = maxWidth / originalWidth;
    const heightRatio = maxHeight / originalHeight;

    scaleFactor = Math.min(widthRatio, heightRatio);
  }

  const width = Math.fround(originalWidth * scaleFactor);
  const height = Math.fround(originalHeight * scaleFactor);

  return { width, height };
}

export function openViewer(filePath: string) {
  window.open(filePath, '_blank');
}

export function formatJalali(date: any) {
  if (!date) return '';
  const jalaliDate = numbersToPersian(new Date(date).toLocaleDateString('fa-IR'));
  return jalaliDate;
}

export async function fetchUrlPreview(url: string) {}
